import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useSuppliersStore from 'modules/Suppliers/stores/suppliersStore';
import useInstallationsStore from 'modules/Installations/stores/installationsStore';
import useEmissionsPageStore from 'modules/Emissions/stores/useEmissionsPageStore';
import { useImportsStore } from 'modules/Imports/stores/importsStore';
import useReportsStore from 'modules/Reports/stores/reportsStore';

/**
 * Leaving a page closes that page's modals.
 *
 * The data pages keep their modal flags in a zustand store so the page header
 * can open a flow that mounts inside the table. Those stores are MODULE-global
 * and unmounting the page doesn't clear them, so a flow abandoned by navigating
 * away reopens on the next visit, under whatever the user opens on arrival.
 *
 * A listener, not a per-page unmount cleanup: that cleanup also fires on
 * StrictMode's simulated remount, after the `?open=` effect has opened the
 * flow — the deep links would close themselves in dev. Register once, inside
 * the Router.
 */

/** Every store that gates a page-scoped modal from module-global state. */
const MODAL_STORES = [
  useSuppliersStore,
  useInstallationsStore,
  useEmissionsPageStore,
  useImportsStore,
  useReportsStore,
];

export const useCloseModalsOnNavigate = () => {
  const history = useHistory();

  useEffect(() => {
    let from = history.location.pathname;

    return history.listen((location) => {
      // PATHNAME only. A search-only change is how the `?open=` aliases clean
      // up after themselves — `history.replace('/suppliers')` the moment the
      // flag is read, so a refresh doesn't reopen the flow — and closing on
      // that would shut the modal the link had just opened.
      if (location.pathname === from) return;
      from = location.pathname;
      MODAL_STORES.forEach((store) => store.getState().closeAllModals());
    });
  }, [history]);
};

export default useCloseModalsOnNavigate;
